'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { APOLLO_FETCH_POLICY } from '@/constants/apollo';
import { setDefaultHeader } from '@/publicSearch/graphql/headers';
import { useGetAnimalEbvsLazyQuery } from '@/publicSearch/graphql/queries.generated';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { useIsEbvEnabled } from '@/hooks/publicSearch/useIsEbvEnabled';
import { useCurrencySymbol } from '@/hooks/publicSearch/useCurrencySymbol';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import { useEbvTraitExplanations } from '@/components/publicSearch/animal/ebv/useEbvTraitExplanations';
import {
  FieldOption,
  FilterInput,
  GetAnimalsRow,
  LogicalOperator,
  OperatorOption,
} from '@/components/publicSearch/constants';
import { normalizeLegacyPercentileFilters } from '@/components/publicSearch/search/ebvPercentileFilterHelpers';
import {
  isFilterInputIncompleteForQuery,
  tryExpandFilterInputToComparisonRows,
} from '@/components/publicSearch/search/searchFilterEbvExpansion';
import {
  isIdentChipQuickSearchField,
  isIdentChipStyleFilter,
} from '@/components/publicSearch/search/animalIdentChipFilter';
import {
  processIdentChipFilterToGql,
  type GqlFilterSubGroup,
} from '@/components/publicSearch/search/identChipGql';
import {
  generateEbvColumnGroupingModel,
  ebvGroupingOptionsFromDisplay,
  parseEbvAverage,
  parseEbvConfig,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import {
  buildAnimalEbvColumns,
  buildAnimalEbvRowsWithAverageLabel,
  buildAnimalEbvSortOptions,
  parseAnimalSearchFieldsConfig,
  transformFieldName,
} from './animalEbvGrid';
import type {
  AnimalEbvFilterField,
  AnimalEbvFilteredFilterOptionInput,
  AnimalEbvFilteredSortOptionInput,
  ComparisonOperator,
  FilterGroupOfAnimalEbvFilteredFilterOptionInput,
} from '@/graphqlClient/types';
import type { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';

function mapProgenyIdentChipSubGroup(
  sg: GqlFilterSubGroup,
): FilterGroupOfAnimalEbvFilteredFilterOptionInput {
  return {
    filters: sg.filters.map((f) => ({
      field: f.field as AnimalEbvFilterField,
      operator: f.operator as ComparisonOperator,
      value: f.value,
    })),
    groups: sg.groups?.map(mapProgenyIdentChipSubGroup),
    logicalOperator: sg.logicalOperator as typeof LogicalOperator.or,
  };
}

function parseMultiSelectValues(value: string) {
  return Array.from(
    new Set(
      value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  );
}

type UseAnimalProgenyEbvFilterProps = {
  animalId: string;
  orgId: string;
  orgCode: string;
  enabled: boolean;
  paginationModel: GridPaginationModel;
  gridSortModel: GridSortModel;
  filters: FilterInput[];
  commonFilterOperator: LogicalOperator;
  showEBVs: boolean;
  showEBVAccuracy: boolean;
  showPercentiles: boolean;
  isCardView: boolean;
  onAverageRowClick?: () => void;
};

export function useAnimalProgenyEbvFilter({
  animalId,
  orgId,
  orgCode,
  enabled,
  paginationModel,
  gridSortModel,
  filters,
  commonFilterOperator,
  showEBVs,
  showEBVAccuracy,
  showPercentiles,
  isCardView,
  onAverageRowClick,
}: UseAnimalProgenyEbvFilterProps) {
  const society = usePublicSearchContext((context) => context.society);
  const isEbvEnabled = useIsEbvEnabled();

  const [cachedTotalCount, setCachedTotalCount] = useState<number | null>(null);
  const previousFilterSignature = useRef<string | null>(null);

  const [getAnimalEbvs, { data, error, loading, previousData }] =
    useGetAnimalEbvsLazyQuery({
      fetchPolicy: APOLLO_FETCH_POLICY.CACHE_FIRST,
      nextFetchPolicy: APOLLO_FETCH_POLICY.CACHE_FIRST,
    });

  const ebvConfig = useMemo(
    () => parseEbvConfig(society.settings),
    [society.settings],
  );
  const effectiveEbvConfig = useMemo(
    () => (isEbvEnabled ? ebvConfig : []),
    [isEbvEnabled, ebvConfig],
  );
  const ebvAverage = useMemo(
    () => parseEbvAverage(society.settings),
    [society.settings],
  );

  // Parse ANIMAL_LIST_FIELDS from society settings so the progeny grid shows
  // the same society-specific columns as the main animal search.
  // Without this the grid falls back to the full default AnimalFields list.
  const animalSearchFieldsConfig = useMemo(
    () => parseAnimalSearchFieldsConfig(society.settings),
    [society.settings],
  );

  const currencySymbol = useCurrencySymbol(society);
  const {
    ebvLabel,
    showAccuracy,
    showBreedAverages,
    showPercentileSearch,
    showBreedplanHelp,
    isBlackRedAngus,
    useColourSlotGrouping,
  } = useEbvDisplayOptions();
  const effectiveShowEBVAccuracy = showEBVAccuracy && showAccuracy;
  const effectiveShowPercentiles = showPercentiles && showPercentileSearch;
  const effectiveEbvAverage = showBreedAverages ? ebvAverage : null;
  const { traitHeaderContext, popoverElement: ebvTraitExplanationPopover } =
    useEbvTraitExplanations(society.settings, showBreedplanHelp);

  const filterGroups = useMemo<
    FilterGroupOfAnimalEbvFilteredFilterOptionInput[] | null
  >(() => {
    if (!orgId || !animalId) return null;

    const processedFilters: AnimalEbvFilteredFilterOptionInput[] = [];
    const sexGroups: FilterGroupOfAnimalEbvFilteredFilterOptionInput[] = [];
    const chipSubGroups: FilterGroupOfAnimalEbvFilteredFilterOptionInput[] = [];

    const normalizedFilters = normalizeLegacyPercentileFilters(filters);

    for (const { field, operator, value } of normalizedFilters) {
      if (!field) continue;

      // Animal / dam / sire chip-style picker: a single chip emits one
      // comparison row; multiple chips become an OR sub-group so progeny
      // matching any of the chip patterns are still narrowed by the parent
      // SIRE_ID / DAM_ID filter outside this loop.
      if (
        isIdentChipQuickSearchField(field) &&
        typeof value === 'string' &&
        isIdentChipStyleFilter(operator, value)
      ) {
        const { processed: chipRows, subGroups: chipGroups } =
          processIdentChipFilterToGql(field, value, transformFieldName);
        if (chipRows.length === 0 && chipGroups.length === 0) continue;
        for (const row of chipRows) {
          processedFilters.push({
            field: row.field as AnimalEbvFilterField,
            operator: row.operator as ComparisonOperator,
            value: row.value,
          });
        }
        for (const sg of chipGroups) {
          chipSubGroups.push(mapProgenyIdentChipSubGroup(sg));
        }
        continue;
      }

      if (
        field === FieldOption.sex.value &&
        operator &&
        typeof value === 'string'
      ) {
        const selectedValues = parseMultiSelectValues(value);
        if (selectedValues.length > 1) {
          sexGroups.push({
            filters: selectedValues.map((selectedValue) => ({
              field: transformFieldName(field) as AnimalEbvFilterField,
              operator: operator as ComparisonOperator,
              value: selectedValue,
            })),
            logicalOperator:
              operator === OperatorOption.notEquals.value
                ? LogicalOperator.and
                : LogicalOperator.or,
          });
          continue;
        }
      }

      const expandedRows = tryExpandFilterInputToComparisonRows({
        field,
        operator,
        value,
      });
      if (expandedRows !== null) {
        for (const row of expandedRows) {
          processedFilters.push({
            field: transformFieldName(row.field) as AnimalEbvFilterField,
            operator: row.operator as ComparisonOperator,
            value: row.value,
          });
        }
        continue;
      }

      if (
        field &&
        operator &&
        (value || operator === OperatorOption.isNotEmpty.value)
      ) {
        processedFilters.push({
          field: transformFieldName(field) as AnimalEbvFilterField,
          operator: operator as ComparisonOperator,
          value: typeof value === 'string' ? value : String(value),
        });
      }
    }

    const parentFilters = [
      {
        field: 'SIRE_ID' as AnimalEbvFilterField,
        operator: OperatorOption.equals.value as ComparisonOperator,
        value: animalId,
      },
      {
        field: 'DAM_ID' as AnimalEbvFilterField,
        operator: OperatorOption.equals.value as ComparisonOperator,
        value: animalId,
      },
    ];

    const groups: FilterGroupOfAnimalEbvFilteredFilterOptionInput[] = [
      {
        filters: parentFilters,
        logicalOperator: LogicalOperator.or,
      },
    ];

    if (processedFilters.length > 0) {
      groups.push({
        filters: processedFilters,
        logicalOperator: commonFilterOperator,
      });
    }

    if (sexGroups.length > 0) {
      groups.push(...sexGroups);
    }

    if (chipSubGroups.length > 0) {
      groups.push(...chipSubGroups);
    }

    return [
      {
        filters: [],
        groups,
        logicalOperator: LogicalOperator.and,
      },
    ];
  }, [animalId, commonFilterOperator, filters, orgId]);

  const sortOptions = useMemo<AnimalEbvFilteredSortOptionInput[]>(
    () => buildAnimalEbvSortOptions(gridSortModel),
    [gridSortModel],
  );

  const filterSignature = useMemo(() => {
    return JSON.stringify({ filterGroups, sortOptions, orgId, animalId });
  }, [filterGroups, sortOptions, orgId, animalId]);

  const startSearch = useCallback(() => {
    if (!enabled) return;
    // NOTE: do not gate the entire progeny query on isEbvEnabled. The EBV
    // toggle only affects column rendering — the basic progeny list still
    // needs to load even when EBVs are disabled for the society.
    if (!orgId || !filterGroups) return;

    if (
      filters.length > 0 &&
      filters.some((f) => isFilterInputIncompleteForQuery(f))
    ) {
      return;
    }

    const filtersChanged = filterSignature !== previousFilterSignature.current;
    const includeTotalCount = filtersChanged || cachedTotalCount === null;

    if (filtersChanged) {
      setCachedTotalCount(null);
      previousFilterSignature.current = filterSignature;
    }

    getAnimalEbvs({
      variables: {
        requestInput: {
          pagination: {
            currentPage: (paginationModel.page ?? 0) + 1,
            pageSize: paginationModel.pageSize,
          },
          filterGroups,
          sortOptions,
          includeTotalCount,
        },
      },
      context: {
        headers: setDefaultHeader(orgId),
      },
    });
  }, [
    enabled,
    orgId,
    filterGroups,
    filters,
    filterSignature,
    cachedTotalCount,
    paginationModel.page,
    paginationModel.pageSize,
    sortOptions,
    getAnimalEbvs,
  ]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- imperatively fires the Apollo lazy query when search inputs change; startSearch may reset cachedTotalCount as part of triggering an async fetch, not a render-time derivation
    startSearch();
  }, [startSearch]);

  useEffect(() => {
    if (
      data?.animalEbvs?.totalCount !== undefined &&
      data.animalEbvs.totalCount >= 0
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- caches the total count from the async Apollo query response (also set in startSearch); this mirrors async fetched data into state, not a render-time derivation
      setCachedTotalCount(data.animalEbvs.totalCount);
    }
  }, [data?.animalEbvs?.totalCount]);

  const effectiveTotalCount = useMemo(() => {
    const serverCount = data?.animalEbvs?.totalCount;
    if (serverCount !== undefined && serverCount >= 0) {
      return serverCount;
    }
    return cachedTotalCount ?? 0;
  }, [data?.animalEbvs?.totalCount, cachedTotalCount]);

  const columns = useMemo(
    () =>
      buildAnimalEbvColumns({
        orgCode,
        ebvConfig: effectiveEbvConfig,
        animalSearchFieldsConfig,
        showEBVs,
        showEBVAccuracy: effectiveShowEBVAccuracy,
        showPercentiles: effectiveShowPercentiles,
        currencySymbol,
        ebvLabel,
        onAverageRowClick:
          isEbvEnabled && showPercentileSearch && effectiveEbvConfig.length > 0
            ? onAverageRowClick
            : undefined,
        traitHeaderContext,
        isBlackRedAngusStrain: isBlackRedAngus,
        ebvAverage: effectiveEbvAverage,
        showPercentileSearch,
        societyId: orgId,
      }),
    [
      orgCode,
      effectiveEbvConfig,
      animalSearchFieldsConfig,
      showEBVs,
      effectiveShowEBVAccuracy,
      effectiveShowPercentiles,
      currencySymbol,
      ebvLabel,
      onAverageRowClick,
      isEbvEnabled,
      showPercentileSearch,
      traitHeaderContext,
      isBlackRedAngus,
      effectiveEbvAverage,
      orgId,
    ],
  );

  const columnGroupingModel = useMemo(() => {
    if (effectiveEbvConfig.length === 0) return [];
    return generateEbvColumnGroupingModel(
      effectiveEbvConfig,
      ebvGroupingOptionsFromDisplay({ useColourSlotGrouping }),
    );
  }, [effectiveEbvConfig, useColourSlotGrouping]);

  // Same `data ?? previousData` pattern as useAnimalEbvFilter — keep the
  // grid populated with the prior progeny rows during refetches instead
  // of clearing them on every loading flip. See useMembersFilter for the
  // full rationale.
  const rows = useMemo(() => {
    return buildAnimalEbvRowsWithAverageLabel({
      items: (data ?? previousData)?.animalEbvs?.items,
      ebvConfig: effectiveEbvConfig,
      ebvAverage: effectiveEbvAverage,
      showEBVs,
      showEBVAccuracy: effectiveShowEBVAccuracy,
      showPercentiles: effectiveShowPercentiles,
      isCardView,
      currencySymbol,
      isBlackRedAngus,
      showPercentileSearch,
    });
  }, [
    data,
    previousData,
    effectiveEbvConfig,
    effectiveEbvAverage,
    isCardView,
    showEBVs,
    effectiveShowEBVAccuracy,
    effectiveShowPercentiles,
    currencySymbol,
    isBlackRedAngus,
    showPercentileSearch,
  ]);

  return useMemo(
    () => ({
      data: data?.animalEbvs
        ? { ...data.animalEbvs, totalCount: effectiveTotalCount }
        : undefined,
      dataGrid: {
        columns,
        rows,
        columnGroupingModel,
        getRowId: (row: GetAnimalsRow) => row.ID,
      },
      done: (data || error) && !loading,
      error,
      loading,
      previousData: previousData?.animalEbvs,
      startSearch,
      ebvTraitExplanationPopover,
    }),
    [
      columnGroupingModel,
      columns,
      data,
      effectiveTotalCount,
      error,
      loading,
      previousData,
      rows,
      startSearch,
      ebvTraitExplanationPopover,
    ],
  );
}
